/**
 * Client-side index of the building opening hours that the calendar already fetches for
 * its background events. Lets selectAllow refuse a drag while it is still happening
 * (FullCalendar's native "not allowed" cursor) instead of opening a modal that can only
 * fail server-side with "Das Gebäude ist derzeit geschlossen.".
 *
 * Fail-open by design: a room without indexed data is treated as open, so a not-yet-loaded
 * day can never block a legitimate selection - the server stays the source of truth.
 */
export function createOpeningHoursIndex() {
	/** @type {Map<string, {start: number, end: number}[]>} */
	const sessionsByRoom = new Map();

	/**
	 * Drop all indexed sessions. Call before re-indexing a freshly fetched date range.
	 */
	function reset() {
		sessionsByRoom.clear();
	}

	/**
	 * Mark a room as indexed without adding an open session (= closed for that day).
	 * @param {number|string} roomId
	 * @returns {{start: number, end: number}[]}
	 */
	function markLoaded(roomId) {
		const key = String(roomId);
		if (!sessionsByRoom.has(key)) {
			sessionsByRoom.set(key, []);
		}

		return sessionsByRoom.get(key);
	}

	/**
	 * @param {number|string} roomId
	 * @param {string|Date} start
	 * @param {string|Date} end
	 */
	function addSession(roomId, start, end) {
		const startMs = new Date(start).getTime();
		const endMs = new Date(end).getTime();
		const sessions = markLoaded(roomId);

		if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
			return;
		}

		sessions.push({ start: startMs, end: endMs });
		sessions.sort((a, b) => a.start - b.start);
	}

	/**
	 * Is [start, end) fully covered by the room's open sessions?
	 * @param {number|string} roomId
	 * @param {Date} start
	 * @param {Date} end
	 * @returns {boolean}
	 */
	function isRangeOpen(roomId, start, end) {
		const sessions = sessionsByRoom.get(String(roomId));
		if (!sessions) {
			return true; // no data for this room yet - never block on missing information
		}

		const target = end.getTime();
		let cursor = start.getTime();

		for (const session of sessions) {
			if (session.start > cursor) {
				break; // gap: the range leaves the opening hours
			}
			if (session.end > cursor) {
				cursor = session.end;
			}
			if (cursor >= target) {
				return true;
			}
		}

		return cursor >= target;
	}

	return { reset, markLoaded, addSession, isRangeOpen };
}
